/**
 * Org Custom Domains settings page — b2b-4-1
 *
 * Route: /org/settings/domains
 *
 * Features:
 * - View current custom domain and its status
 * - Register a new custom domain (calls POST /api/v1/orgs/domains)
 * - Delete the current domain (calls DELETE /api/v1/orgs/domains)
 * - Status badge: pending / active / failed / disabled
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DomainRecord {
  id: number;
  org_id: number;
  hostname: string;
  status: 'pending' | 'active' | 'failed' | 'disabled';
  cf_hostname_id: string | null;
  created_at: string | null;
  verified_at: string | null;
  updated_at: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const statusColor: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  failed: 'bg-red-100 text-red-800',
  disabled: 'bg-gray-100 text-gray-500',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColor[status] ?? 'bg-gray-100 text-gray-600'}`}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function OrgDomains() {
  const qc = useQueryClient();
  const [orgId, setOrgId] = useState<number | null>(null);
  const [hostname, setHostname] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Read org_id from localStorage (set during login / org context)
  React.useEffect(() => {
    const stored = localStorage.getItem('org_id');
    if (stored) setOrgId(Number(stored));
  }, []);

  // Fetch current domain
  const { data: domain, isLoading } = useQuery<DomainRecord | null>({
    queryKey: ['org-domain', orgId],
    queryFn: async () => {
      if (!orgId) return null;
      const res = await api.get(`/api/v1/orgs/domains?org_id=${orgId}`);
      return res.data ?? null;
    },
    enabled: !!orgId,
  });

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: async (h: string) => {
      const res = await api.post('/api/v1/orgs/domains', {
        hostname: h,
        org_id: orgId,
      });
      return res.data as DomainRecord;
    },
    onSuccess: () => {
      setSuccess('Custom domain registered. DNS verification may take a few minutes.');
      setHostname('');
      setError('');
      qc.invalidateQueries({ queryKey: ['org-domain', orgId] });
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Registration failed.';
      setError(msg);
      setSuccess('');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete('/api/v1/orgs/domains', { data: { org_id: orgId } });
    },
    onSuccess: () => {
      setSuccess('Custom domain removed.');
      setError('');
      qc.invalidateQueries({ queryKey: ['org-domain', orgId] });
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Deletion failed.';
      setError(msg);
      setSuccess('');
    },
  });

  const handleRegister = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!hostname.trim()) {
      setError('Please enter a hostname.');
      return;
    }
    registerMutation.mutate(hostname.trim().toLowerCase());
  };

  const handleDelete = () => {
    if (!window.confirm('Remove this custom domain? The TLS certificate will be revoked.')) return;
    deleteMutation.mutate();
  };

  if (!orgId) {
    return (
      <div className="p-6 text-gray-500">
        Organisation context not found. Please log in with an organisation account.
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Custom Domain</h1>
        <p className="mt-1 text-sm text-gray-500">
          Map your organisation's subdomain to this platform. A TLS certificate will be
          provisioned automatically via Cloudflare SSL for SaaS.
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-4 py-3 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 rounded-md px-4 py-3 text-sm">
          {success}
        </div>
      )}

      {/* Current domain */}
      {isLoading ? (
        <div className="text-gray-400 text-sm">Loading…</div>
      ) : domain ? (
        <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium text-gray-700">Current Domain</h2>
            <StatusBadge status={domain.status} />
          </div>
          <p className="text-base font-mono text-gray-900">{domain.hostname}</p>
          {domain.status === 'pending' && (
            <p className="text-xs text-yellow-700 bg-yellow-50 rounded px-3 py-2">
              Waiting for DNS verification. Create a CNAME record pointing{' '}
              <strong>{domain.hostname}</strong> to your platform's Cloudflare hostname, then wait
              up to 15 minutes.
            </p>
          )}
          {domain.status === 'active' && domain.verified_at && (
            <p className="text-xs text-gray-400">
              Verified at {new Date(domain.verified_at).toLocaleString()}
            </p>
          )}
          <button
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="mt-2 inline-flex items-center px-3 py-1.5 border border-red-300 text-red-600 text-xs rounded hover:bg-red-50 disabled:opacity-50"
          >
            {deleteMutation.isPending ? 'Removing…' : 'Remove domain'}
          </button>
        </div>
      ) : (
        /* Register form */
        <form onSubmit={handleRegister} className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
          <h2 className="text-sm font-medium text-gray-700">Register Custom Domain</h2>
          <div>
            <label htmlFor="hostname" className="block text-xs font-medium text-gray-600 mb-1">
              Hostname (e.g. invest.acmecapital.co.za)
            </label>
            <input
              id="hostname"
              type="text"
              value={hostname}
              onChange={e => setHostname(e.target.value)}
              placeholder="invest.acmecapital.co.za"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <p className="text-xs text-gray-400">
            After registering, create a CNAME DNS record for your hostname pointing to the
            platform's Cloudflare zone hostname. TLS verification will happen automatically.
          </p>
          <button
            type="submit"
            disabled={registerMutation.isPending}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {registerMutation.isPending ? 'Registering…' : 'Register domain'}
          </button>
        </form>
      )}
    </div>
  );
}
